import IonToolbar from '../../../../../components/Modal/ModalToolbar';
import { Toast } from '@capacitor/toast';
import { IonButton, IonButtons, IonContent, IonFooter, IonHeader, IonIcon, IonInput, IonLoading, IonSegment, IonSegmentButton, IonSelect, IonSelectOption, IonTextarea, IonTitle, IonToggle } from '@ionic/react';
import {
  addOutline,
  arrowBack,
  arrowDownOutline,
  arrowUpOutline,
  cogOutline,
  ellipseOutline,
  fastFoodOutline,
  imagesOutline,
  layersOutline,
  listOutline,
  pizzaOutline,
  pricetagOutline,
  sparklesOutline,
  storefrontOutline,
  trashOutline,
  warningOutline,
} from 'ionicons/icons';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MessageContext } from '../../../../../contexts/MessageContext';
import {
  confirmarCatalogoProdutosEmpresaImportacao,
  presignedUrlCatalogoIa,
  statusJobCatalogoIa,
  uploadCatalogoProdutosEmpresaIa,
} from '../../../../../Utils/Services';
import './UploadIA.css';
import ProdutoImagemPreview from './ProdutoImagemPreview';
import { revisarPrecoImportacao } from './precoImportacao';
import { catalogoParaConfirmacao, estimarTamanhoCatalogo, LIMITE_CATALOGO_IMPORTACAO_BYTES, MENSAGEM_CATALOGO_ACIMA_LIMITE, normalizarPreviewCatalogo, novoIdPreview, removerReferenciasPreview, sincronizarPizzas, Categoria, GrupoComplemento, ItemComplemento, Tamanho, Borda, Massa, Produto, Preview } from './catalogoPreview';

export type CatalogoImportacaoPreviewProps = {
  catalogo: any;
  source: string;
  titulo?: string;
  onBack: () => void;
  onConfirmed: (attrs: any) => void;
};

type Props = {
  closeAction: Function;
  closeActionSimple: Function;
  importacao?: Pick<CatalogoImportacaoPreviewProps, 'catalogo' | 'source' | 'titulo'>;
};

const MAX_FILES = 6;
const novoId = novoIdPreview;

const UploadIA: React.FC<Props> = (props: Props) => {
  const { loja } = useContext(MessageContext);
  const [fase, setFase] = useState<'upload' | 'preview'>(props.importacao ? 'preview' : 'upload');
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [showLoading, setShowLoading] = useState(false);
  const [mensagemLoading, setMensagemLoading] = useState('Processando...');
  const [preview, setPreview] = useState<Preview>(() => props.importacao ? normalizarPreviewCatalogo(props.importacao.catalogo) : { categorias: [], produtos: [] });
  const [erroConfirmacao, setErroConfirmacao] = useState('');
  const tamanhoEstimado = useMemo(() => estimarTamanhoCatalogo(preview, loja), [preview, loja]);
  const excedeLimite = tamanhoEstimado > LIMITE_CATALOGO_IMPORTACAO_BYTES;
  const [falhas, setFalhas] = useState<{ idx: number; erro: string }[]>([]);
  const [catAbertos, setCatAbertos] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const mounted = useRef(true);
  const confirmando = useRef(false);
  const activeStore = useRef(loja);
  activeStore.current = loja;
  const previousStore = useRef(loja);
  const requestSequence = useRef(0);
  useEffect(() => {
    if (previousStore.current === loja) return;
    previousStore.current = loja;
    requestSequence.current += 1;
    confirmando.current = false;
    setShowLoading(false);
    setArquivos([]);
    setPreviews([]);
    setPreview({ categorias: [], produtos: [] });
    setErroConfirmacao('');
    setCatAbertos([]);
    setFalhas([]);
    if (!props.importacao) setFase('upload');
  }, [loja, props.importacao]);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => () => { previews.forEach(url => URL.revokeObjectURL(url)); }, [previews]);
  const voltar = () => {
    if (!confirmando.current) props.closeActionSimple();
  };

  const getToken = () => {
    const tkn = localStorage.getItem('token');
    return typeof tkn === 'string' ? tkn : '';
  };

  const onSelecionarArquivos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const validos = files.filter((f) => f.type.startsWith('image/'));
    const combinado = [...arquivos, ...validos].slice(0, MAX_FILES);
    setArquivos(combinado);
    setPreviews(combinado.map((f) => URL.createObjectURL(f)));
    if (e.target) e.target.value = '';
  };

  const removerArquivo = (idx: number) => {
    const novos = arquivos.filter((_, i) => i !== idx);
    setArquivos(novos);
    setPreviews(novos.map((f) => URL.createObjectURL(f)));
  };

  const interpretar = async () => {
    if (showLoading || previousStore.current !== loja) return;
    if (arquivos.length === 0) {
      Toast.show({ text: 'Selecione ao menos 1 foto do cardápio.', position: 'center', duration: 'long' });
      return;
    }
    const lojaSolicitada = loja;
    const requestId = ++requestSequence.current;
    const isAtual = () => mounted.current && activeStore.current === lojaSolicitada && requestSequence.current === requestId;
    try {
      setMensagemLoading('Enviando fotos...');
      setShowLoading(true);
      const token = getToken();
      const lojaParam = loja.replaceAll(/#/g, '%23');

      // 1) Solicita URLs presigned
      const respUrls = await fetch(presignedUrlCatalogoIa.url.replaceAll('{id}', lojaParam), {
        method: presignedUrlCatalogoIa.method,
        headers: [['token', token]],
        body: JSON.stringify({
          arquivos: arquivos.map((f) => ({ contentType: f.type || 'image/jpeg' })),
        }),
        credentials: 'include',
      });
      const dadosUrls = await respUrls.json();
      if (!respUrls.ok || !dadosUrls || !Array.isArray(dadosUrls.arquivos)) {
        throw new Error(dadosUrls?.message || 'Falha ao gerar URLs de upload.');
      }

      // 2) Upload paralelo para o S3
      if (!isAtual()) return;
      setMensagemLoading('Subindo fotos...');
      await Promise.all(
        dadosUrls.arquivos.map((item: any, i: number) =>
          fetch(item.uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': item.contentType },
            body: arquivos[i],
          }).then((r) => {
            if (!r.ok) throw new Error('Falha no upload da imagem ' + (i + 1));
          })
        )
      );

      if (!isAtual()) return;
      // 3) Inicia interpretação IA (assíncrona): backend retorna {jobId}
      setMensagemLoading('A IA está lendo seu cardápio, isso pode levar alguns minutos. Aguarde...');
      const keys = dadosUrls.arquivos.map((a: any) => a.key);
      const respIa = await fetch(uploadCatalogoProdutosEmpresaIa.url.replaceAll('{id}', lojaParam), {
        method: uploadCatalogoProdutosEmpresaIa.method,
        headers: [
          ['token', token],
          ['Content-Type', 'application/json'],
        ],
        body: JSON.stringify({ keys }),
        credentials: 'include',
      });
      const dadosIa = await respIa.json();
      if (!isAtual()) return;
      if (!respIa.ok || !dadosIa || !dadosIa.jobId) {
        throw new Error(dadosIa?.message || 'Falha ao iniciar processamento com IA.');
      }

      // 4) Polling do job a cada 3s, com timeout de 5min
      const jobId = dadosIa.jobId;
      const inicio = Date.now();
      const TIMEOUT_MS = 5 * 60 * 1000;
      const INTERVALO_MS = 5000;
      let resultado: any = null;

      while (isAtual()) {
        if (Date.now() - inicio > TIMEOUT_MS) {
          throw new Error('Tempo esgotado aguardando a IA. Tente com menos fotos.');
        }
        await new Promise((r) => setTimeout(r, INTERVALO_MS));
        if (!isAtual()) return;

        // mensagem estável durante polling para não piscar

        const respStatus = await fetch(
          statusJobCatalogoIa.url.replaceAll('{id}', lojaParam).replaceAll('{jobId}', jobId),
          {
            method: statusJobCatalogoIa.method,
            headers: [['token', token]],
            credentials: 'include',
          }
        );
        const dadosStatus = await respStatus.json();
        if (!isAtual()) return;
        if (!respStatus.ok) throw new Error(dadosStatus?.message || 'Falha ao consultar a leitura do catálogo.');
        if (!dadosStatus || !dadosStatus.status) continue;

        if (dadosStatus.status === 'concluido') {
          resultado = dadosStatus;
          break;
        }
        if (dadosStatus.status === 'erro') {
          throw new Error(dadosStatus.mensagem || 'A IA não conseguiu processar as fotos.');
        }
        // status === 'processando' — continua
      }

      if (!isAtual()) return;
      if (!resultado || !resultado.preview) {
        throw new Error('A IA não retornou resultado.');
      }
      const normalizado = normalizarPreviewCatalogo(resultado.preview);
      if (!isAtual()) return;
      setPreview(normalizado);
      setCatAbertos([]);
      setFalhas(resultado.falhas || []);
      setFase('preview');
      setShowLoading(false);
    } catch (err: any) {
      if (!isAtual()) return;
      console.log('[UploadIA] erro interpretar', err);
      Toast.show({ text: err?.message || 'Erro ao interpretar cardápio.', position: 'center', duration: 'long' });
      setShowLoading(false);
    }
  };

  const confirmar = async () => {
    if (confirmando.current || previousStore.current !== loja) return;
    if (excedeLimite) {
      Toast.show({ text: MENSAGEM_CATALOGO_ACIMA_LIMITE, position: 'center', duration: 'long' });
      return;
    }
    if (preview.categorias.length === 0 && preview.produtos.length === 0) {
      Toast.show({ text: 'Não há itens para salvar.', position: 'center', duration: 'long' });
      return;
    }
    const pendente = preview.produtos.find(p => revisarPrecoImportacao(p, preview.produtos,
      preview.categorias.find(c => c.id === p.categoriaId)?.tipo).pendente);
    if (pendente) {
      setErroConfirmacao(`Revise "${pendente.nome}": o preço base está zerado e ainda é possível concluir sem uma escolha paga.`);
      setCatAbertos(ids => ids.includes(pendente.categoriaId) ? ids : [...ids, pendente.categoriaId]);
      requestAnimationFrame(() => {
        const card = document.getElementById(`importacao-produto-${pendente.id}`);
        card?.focus({ preventScroll: true });
        card?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
      });
      return;
    }
    confirmando.current = true;
    setErroConfirmacao('');
    const lojaSolicitada = loja;
    const requestId = ++requestSequence.current;
    const isAtual = () => mounted.current && activeStore.current === lojaSolicitada && requestSequence.current === requestId;
    try {
      const payload = catalogoParaConfirmacao(preview.categorias, preview.produtos);
      setMensagemLoading('Salvando catálogo...');
      setShowLoading(true);
      const token = getToken();
      const lojaParam = loja.replaceAll(/#/g, '%23');
      const resp = await fetch(confirmarCatalogoProdutosEmpresaImportacao.url.replaceAll('{id}', lojaParam), {
        method: confirmarCatalogoProdutosEmpresaImportacao.method,
        headers: [
          ['token', token],
          ['Content-Type', 'application/json'],
        ],
        body: JSON.stringify({ source: props.importacao?.source || 'IA', preview: payload }),
        credentials: 'include',
      });
      const dados = await resp.json();
      if (!isAtual()) return;
      if (!resp.ok || !dados?.message?.Attributes) {
        throw new Error(typeof dados?.message === 'string' ? dados.message : 'Erro ao salvar o catálogo.');
      }
      Toast.show({ text: 'Catálogo importado com sucesso!', position: 'center', duration: 'long' });
      props.closeAction(dados.message.Attributes);
    } catch (err: any) {
      if (!isAtual()) return;
      setErroConfirmacao(err?.message || 'Erro ao salvar.');
      Toast.show({ text: err?.message || 'Erro ao salvar.', position: 'center', duration: 'long' });
    } finally {
      if (isAtual()) {
        confirmando.current = false;
        setShowLoading(false);
      }
    }
  };

  // ====================== MUTAÇÕES (estado normalizado) ======================

  const setCatalog = (updater: (s: Preview) => Preview) => {
    setErroConfirmacao('');
    setPreview((s) => updater(s));
  };

  // Categoria
  const addCategoria = () => {
    const novoIdCat = novoId();
    setCatalog((s) => ({
      ...s,
      categorias: [
        ...s.categorias,
        {
          id: novoIdCat,
          nome: 'Nova categoria',
          tipo: 'simples',
          cobrarMaiorValor: true,
          tamanhos: [],
          bordas: [],
          massas: [],
        },
      ],
    }));
    setCatAbertos((prev) => [...prev, novoIdCat]);
  };

  const updCategoria = (id: string, patch: Partial<Categoria>) =>
    setCatalog((s) => {
      const categorias = s.categorias.map((c) => (c.id === id ? { ...c, ...patch } : c));
      const target = categorias.find((c) => c.id === id);
      if (target && target.tipo === 'pizzas' && target.tamanhos.length === 0) {
        target.tamanhos = [{ _previewKey: novoId(), name: 'Único', sabores: 1 }];
      }
      const produtos = sincronizarPizzas(categorias, s.produtos, s.categorias);
      return { categorias, produtos };
    });

  const remCategoria = (id: string) =>
    setCatalog(s => removerReferenciasPreview(s, s.produtos.filter(p => p.categoriaId === id).map(p => p.id), [id]));

  const moverCategoria = (id: string, dir: -1 | 1) =>
    setCatalog((s) => {
      const idx = s.categorias.findIndex((c) => c.id === id);
      const tgt = idx + dir;
      if (idx < 0 || tgt < 0 || tgt >= s.categorias.length) return s;
      const categorias = [...s.categorias];
      [categorias[idx], categorias[tgt]] = [categorias[tgt], categorias[idx]];
      return { ...s, categorias };
    });

  // Tamanhos / Bordas / Massas (afetam produtos via sincronizarPizzas quando aplicável)
  const addTamanho = (catId: string) =>
    setCatalog((s) => {
      const categorias = s.categorias.map((c) =>
        c.id === catId
          ? { ...c, tamanhos: [...c.tamanhos, { _previewKey: novoId(), name: `Tamanho ${c.tamanhos.length + 1}`, sabores: 1 }] }
          : c
      );
      return { categorias, produtos: sincronizarPizzas(categorias, s.produtos, s.categorias) };
    });

  const updTamanho = (catId: string, idx: number, patch: Partial<Tamanho>) =>
    setCatalog((s) => {
      const categorias = s.categorias.map((c) =>
        c.id === catId ? { ...c, tamanhos: c.tamanhos.map((t, i) => (i === idx ? { ...t, ...patch } : t)) } : c
      );
      return { categorias, produtos: sincronizarPizzas(categorias, s.produtos, s.categorias) };
    });

  const remTamanho = (catId: string, idx: number) =>
    setCatalog((s) => {
      const categorias = s.categorias.map((c) =>
        c.id === catId ? { ...c, tamanhos: c.tamanhos.filter((_, i) => i !== idx) } : c
      );
      return { categorias, produtos: sincronizarPizzas(categorias, s.produtos, s.categorias) };
    });

  const addBorda = (catId: string) =>
    setCatalog((s) => ({
      ...s,
      categorias: s.categorias.map((c) =>
        c.id === catId ? { ...c, bordas: [...c.bordas, { name: `Borda ${c.bordas.length + 1}`, price: '0,00' }] } : c
      ),
    }));
  const updBorda = (catId: string, idx: number, patch: Partial<Borda>) =>
    setCatalog((s) => ({
      ...s,
      categorias: s.categorias.map((c) =>
        c.id === catId ? { ...c, bordas: c.bordas.map((b, i) => (i === idx ? { ...b, ...patch } : b)) } : c
      ),
    }));
  const remBorda = (catId: string, idx: number) =>
    setCatalog((s) => ({
      ...s,
      categorias: s.categorias.map((c) =>
        c.id === catId ? { ...c, bordas: c.bordas.filter((_, i) => i !== idx) } : c
      ),
    }));

  const addMassa = (catId: string) =>
    setCatalog((s) => ({
      ...s,
      categorias: s.categorias.map((c) =>
        c.id === catId ? { ...c, massas: [...c.massas, { name: `Massa ${c.massas.length + 1}`, price: '0,00' }] } : c
      ),
    }));
  const updMassa = (catId: string, idx: number, patch: Partial<Massa>) =>
    setCatalog((s) => ({
      ...s,
      categorias: s.categorias.map((c) =>
        c.id === catId ? { ...c, massas: c.massas.map((m, i) => (i === idx ? { ...m, ...patch } : m)) } : c
      ),
    }));
  const remMassa = (catId: string, idx: number) =>
    setCatalog((s) => ({
      ...s,
      categorias: s.categorias.map((c) =>
        c.id === catId ? { ...c, massas: c.massas.filter((_, i) => i !== idx) } : c
      ),
    }));

  // Produtos
  const addProduto = (catId: string) =>
    setCatalog((s) => {
      const cat = s.categorias.find((c) => c.id === catId);
      const prod: Produto = {
        id: novoId(),
        categoriaId: catId,
        nome: 'Novo produto',
        descricao: '',
        valor: cat?.tipo === 'pizzas' ? '' : '0,00',
        valores:
          cat?.tipo === 'pizzas'
            ? cat.tamanhos.map((t) => ({ name: t.name, valor: '', sabores: t.sabores }))
            : [],
        grupoComplementos: [],
      };
      return { ...s, produtos: [...s.produtos, prod] };
    });

  const updProduto = (id: string, patch: Partial<Produto>) =>
    setCatalog(s => {
      const produtos = s.produtos.map(p => p.id === id ? { ...p, ...patch } : p);
      return { ...s, produtos: patch.categoriaId ? sincronizarPizzas(s.categorias, produtos) : produtos };
    });

  const remProduto = (id: string) =>
    setCatalog(s => removerReferenciasPreview(s, [id]));

  const moverProduto = (id: string, dir: -1 | 1) =>
    setCatalog((s) => {
      const idx = s.produtos.findIndex((p) => p.id === id);
      if (idx < 0) return s;
      const catId = s.produtos[idx].categoriaId;
      const indicesNaCat = s.produtos.reduce<number[]>((acc, p, i) => {
        if (p.categoriaId === catId) acc.push(i);
        return acc;
      }, []);
      const posInCat = indicesNaCat.indexOf(idx);
      const tgtPosInCat = posInCat + dir;
      if (tgtPosInCat < 0 || tgtPosInCat >= indicesNaCat.length) return s;
      const a = indicesNaCat[posInCat];
      const b = indicesNaCat[tgtPosInCat];
      const produtos = [...s.produtos];
      [produtos[a], produtos[b]] = [produtos[b], produtos[a]];
      return { ...s, produtos };
    });

  const updValorPizza = (prodId: string, tamanhoName: string, valor: string) =>
    setCatalog((s) => ({
      ...s,
      produtos: s.produtos.map((p) =>
        p.id === prodId
          ? {
              ...p,
              valores: p.valores.map((v) => (v.name === tamanhoName ? { ...v, valor } : v)),
            }
          : p
      ),
    }));

  // Grupos de complementos
  const addGrupo = (prodId: string) =>
    setCatalog((s) => ({
      ...s,
      produtos: s.produtos.map((p) =>
        p.id === prodId
          ? {
              ...p,
              grupoComplementos: [
                ...p.grupoComplementos,
                { id: novoId(), name: 'Novo grupo', obrigatoriedade: 'opcional', sizeMin: 0, sizeMax: 1, itens: [] },
              ],
            }
          : p
      ),
    }));

  // Aplica regras de coerência num grupo de complementos:
  //  - obrigatório  → sizeMin >= 1
  //  - opcional     → sizeMin >= 0
  //  - máximo 0 ou ausente significa sem limite, como no catálogo existente
  //  - mínimo nunca pode ser maior que um máximo positivo
  const aplicarRegrasGrupo = (g: GrupoComplemento): GrupoComplemento => {
    const obrig = g.obrigatoriedade === 'obrigatorio';
    const sizeMin = obrig ? Math.max(1, Math.floor(Number(g.sizeMin) || 0))
      : g.sizeMin == null ? g.sizeMin : Math.max(0, Math.floor(Number(g.sizeMin) || 0));
    let sizeMax = g.sizeMax == null ? g.sizeMax : Math.max(0, Math.floor(Number(g.sizeMax) || 0));
    if (sizeMax > 0 && sizeMax < sizeMin) sizeMax = sizeMin;
    return { ...g, ...(sizeMin === undefined ? {} : { sizeMin }), ...(sizeMax === undefined ? {} : { sizeMax }) };
  };

  const updGrupo = (prodId: string, gi: number, patch: Partial<GrupoComplemento>) =>
    setCatalog((s) => ({
      ...s,
      produtos: s.produtos.map((p) =>
        p.id === prodId
          ? {
              ...p,
              grupoComplementos: p.grupoComplementos.map((g, i) =>
                i === gi ? aplicarRegrasGrupo({ ...g, ...patch }) : g,
              ),
            }
          : p
      ),
    }));

  const remGrupo = (prodId: string, gi: number) =>
    setCatalog((s) => ({
      ...s,
      produtos: s.produtos.map((p) =>
        p.id === prodId
          ? { ...p, grupoComplementos: p.grupoComplementos.filter((_, i) => i !== gi) }
          : p
      ),
    }));

  const addItemGrupo = (prodId: string, gi: number) =>
    setCatalog((s) => ({
      ...s,
      produtos: s.produtos.map((p) =>
        p.id === prodId
          ? {
              ...p,
              grupoComplementos: p.grupoComplementos.map((g, i) =>
                i === gi ? { ...g, itens: [...g.itens, { optionId: novoId(), name: 'Novo item', price: '0,00', description: '' }] } : g
              ),
            }
          : p
      ),
    }));

  const updItemGrupo = (prodId: string, gi: number, ii: number, patch: Partial<ItemComplemento>) =>
    setCatalog((s) => ({
      ...s,
      produtos: s.produtos.map((p) =>
        p.id === prodId
          ? {
              ...p,
              grupoComplementos: p.grupoComplementos.map((g, i) =>
                i === gi ? { ...g, itens: g.itens.map((it, j) => (j === ii ? { ...it, ...patch } : it)) } : g
              ),
            }
          : p
      ),
    }));

  const remItemGrupo = (prodId: string, gi: number, ii: number) =>
    setCatalog((s) => ({
      ...s,
      produtos: s.produtos.map((p) =>
        p.id === prodId
          ? {
              ...p,
              grupoComplementos: p.grupoComplementos.map((g, i) =>
                i === gi ? { ...g, itens: g.itens.filter((_, j) => j !== ii) } : g
              ),
            }
          : p
      ),
    }));

  // ===== Validações =====
  type WarnMap = {
    categorias: Record<string, string[]>;
    produtos: Record<string, string[]>;
    totalAlertas: number;
  };

  const warnings: WarnMap = useMemo(() => {
    const catW: Record<string, string[]> = {};
    const prodW: Record<string, string[]> = {};
    preview.categorias.forEach((c) => {
      const arr: string[] = [];
      if (!c.nome.trim()) arr.push('Sem nome');
      if (c.tipo === 'pizzas' && c.tamanhos.length === 0) arr.push('Pizzas sem tamanhos');
      if (preview.produtos.filter((p) => p.categoriaId === c.id).length === 0) arr.push('Sem produtos');
      if (arr.length > 0) catW[c.id] = arr;
    });
    preview.produtos.forEach((p) => {
      const cat = preview.categorias.find((c) => c.id === p.categoriaId);
      const arr: string[] = [];
      if (!p.nome.trim()) arr.push('Sem nome');
      if (cat?.tipo === 'pizzas') {
        if (p.valores.length === 0) arr.push('Sem preço por tamanho');
        if (p.valores.some((v) => v.valor === '' || v.valor == null)) arr.push('Preço não informado');
      } else {
        if (p.valor === '' || p.valor == null) arr.push('Preço não informado');
      }
      if (revisarPrecoImportacao(p, preview.produtos, cat?.tipo).pendente) arr.push('Preço base zerado sem escolha obrigatória paga');
      p.grupoComplementos.forEach((g, gi) => {
        if (!g.name || !g.name.trim()) arr.push(`Grupo ${gi + 1}: sem nome`);
        if (g.itens.length === 0) arr.push(`Grupo ${gi + 1}: sem itens`);
        if (g.sizeMin < 0) arr.push(`Grupo ${gi + 1}: mínimo inválido`);
        if (g.sizeMax < 0) arr.push(`Grupo ${gi + 1}: máximo inválido`);
        if (g.sizeMax > 0 && g.sizeMin > g.sizeMax) arr.push(`Grupo ${gi + 1}: mínimo > máximo`);
        if (g.obrigatoriedade === 'obrigatorio' && g.sizeMin < 1)
          arr.push(`Grupo ${gi + 1}: obrigatório exige mínimo ≥ 1`);
      });
      if (arr.length > 0) prodW[p.id] = arr;
    });
    const totalAlertas =
      Object.values(catW).reduce((a, b) => a + b.length, 0) +
      Object.values(prodW).reduce((a, b) => a + b.length, 0);
    return { categorias: catW, produtos: prodW, totalAlertas };
  }, [preview]);

  // ====================== RENDER ======================

  if (fase === 'upload') {
    return (
      <>
        <IonHeader className="ion-no-border">
          <IonToolbar className="uploadia-toolbar">
            <IonButtons slot="start">
              <IonButton fill="clear" aria-label="Voltar" disabled={showLoading} onClick={voltar}><IonIcon slot="icon-only" icon={arrowBack} /></IonButton>
            </IonButtons>
            <IonTitle>
              <IonIcon icon={sparklesOutline} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Importar com IA
            </IonTitle>
          </IonToolbar>
        </IonHeader>

        <IonContent class="ion-padding uploadia-content">
          <div className="uploadia-intro-card">
            <h2>
              <IonIcon icon={sparklesOutline} color="primary" />
              Como funciona
            </h2>
            <p>
              Tire <b>fotos nítidas</b> do seu cardápio (até {MAX_FILES} imagens). A IA vai ler e montar uma prévia
              editável com categorias, produtos, valores e complementos. Você confere e confirma.{' '}
              <b>Esta importação substitui o catálogo atual.</b>
            </p>
          </div>

          <input
            ref={inputRef}
            type="file"
            aria-label="Fotos do cardápio"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={onSelecionarArquivos}
          />
          <button
            type="button"
            disabled={arquivos.length >= MAX_FILES}
            className={
              'uploadia-image-dropzone' +
              (arquivos.length >= MAX_FILES ? ' uploadia-image-dropzone--disabled' : '')
            }
            onClick={() => {
              if (arquivos.length < MAX_FILES) inputRef.current?.click();
            }}
          >
            <div className="uploadia-image-dropzone-icon">
              <IonIcon icon={imagesOutline} />
            </div>
            <div className="uploadia-image-dropzone-title">Selecionar fotos</div>
            <div className="uploadia-image-dropzone-desc">Toque para escolher imagens do cardápio</div>
            <div className="uploadia-image-dropzone-hint">
              {arquivos.length}/{MAX_FILES} fotos selecionadas
            </div>
          </button>

          {previews.length > 0 && (
            <div className="uploadia-foto-grid">
              {previews.map((src, i) => (
                <div key={i} className="uploadia-foto-thumb">
                  <img src={src} alt={'foto-' + i} />
                  <button
                    type="button"
                    className="uploadia-foto-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      removerArquivo(i);
                    }}
                  >
                    <IonIcon icon={trashOutline} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <IonLoading key={mensagemLoading} isOpen={showLoading} message={mensagemLoading} spinner="circles" />
        </IonContent>

        <IonFooter className="ion-no-border uploadia-footer">
          <IonToolbar>
            <IonButton
              expand="block"
              size="large"
              disabled={arquivos.length === 0 || showLoading}
              onClick={() => interpretar()}
            >
              <IonIcon slot="start" icon={sparklesOutline} />
              Importar com IA
            </IonButton>
          </IonToolbar>
        </IonFooter>
      </>
    );
  }

  // ===== FASE PREVIEW =====
  return (
    <>
      <IonHeader className="ion-no-border">
        <IonToolbar className="uploadia-toolbar">
          <IonButtons slot="start">
            <IonButton fill="clear" aria-label="Voltar" disabled={showLoading} onClick={voltar}><IonIcon slot="icon-only" icon={arrowBack} /></IonButton>
          </IonButtons>
          <IonTitle>{props.importacao?.titulo || 'Conferir catálogo'}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent class="ion-padding uploadia-content">
        <div className="uploadia-summary">
          <h2 className="uploadia-summary-title">Confira e edite o catálogo</h2>
          {props.importacao?.catalogo?.resumoImportacao && <p className="uploadia-summary-text">
            Extraídos: {props.importacao.catalogo.resumoImportacao.produtos} produtos em {props.importacao.catalogo.resumoImportacao.categorias} categorias.
            {' '}Na revisão: {preview.produtos.length} produtos. Removidos: {Math.max(0, props.importacao.catalogo.resumoImportacao.produtos - preview.produtos.length)}.
            {' '}Produtos ocultos na origem: {props.importacao.catalogo.resumoImportacao.ocultos || 0}.
          </p>}
          {!!props.importacao?.catalogo?.itensNaoImportados?.length && <div className="uploadia-warning-note" role="alert">
            <div>
              <strong>Prévia parcial do iFood</strong>
              <p>{props.importacao.catalogo.itensNaoImportados.length} produto(s) ficaram de fora porque o iFood bloqueou a consulta dos complementos (403).
                {' '}Ao confirmar, somente os produtos desta prévia substituirão o catálogo atual.</p>
              <details>
                <summary>Ver produtos não importados</summary>
                <ul>{props.importacao.catalogo.itensNaoImportados.map((item: { id?: string; nome: string; categoria?: string }, index: number) =>
                  <li key={`${item.id || index}-${index}`}>{item.nome}{item.categoria ? ` — ${item.categoria}` : ''}</li>
                )}</ul>
              </details>
            </div>
          </div>}
          <div className="uploadia-summary-row">
            <span className="uploadia-summary-chip">
              <IonIcon icon={storefrontOutline} />
              {preview.categorias.length} categoria(s)
            </span>
            <span className="uploadia-summary-chip">
              <IonIcon icon={fastFoodOutline} />
              {preview.produtos.length} produto(s)
            </span>
            {warnings.totalAlertas > 0 && (
              <span className="uploadia-summary-chip uploadia-summary-chip--alert">
                <IonIcon icon={warningOutline} />
                {warnings.totalAlertas} alerta(s)
              </span>
            )}
            {falhas.length > 0 && (
              <span className="uploadia-summary-chip uploadia-summary-chip--danger">
                <IonIcon icon={warningOutline} />
                {falhas.length} imagem(ns) falharam
              </span>
            )}
          </div>
          <p className="uploadia-summary-text">
            Ao confirmar, o catálogo atual será <b>substituído</b>.
          </p>
          <p className="uploadia-summary-text" role="status">
            Tamanho estimado: {tamanhoEstimado.toLocaleString('pt-BR')} bytes de {LIMITE_CATALOGO_IMPORTACAO_BYTES.toLocaleString('pt-BR')} bytes.
            {' '}O tamanho final será verificado ao confirmar.
          </p>
          {excedeLimite && <p className="uploadia-warning-note" role="alert">{MENSAGEM_CATALOGO_ACIMA_LIMITE}</p>}
          {erroConfirmacao && <p className="uploadia-warning-note" role="alert">
            {erroConfirmacao} Sua revisão foi mantida. Ajuste o catálogo nesta prévia e tente confirmar novamente.
          </p>}
        </div>

        <div className="uploadia-actions-top">
          <IonButton size="small" onClick={() => addCategoria()}>
            <IonIcon slot="start" icon={addOutline} />
            Adicionar categoria
          </IonButton>
        </div>

        <div>
          {preview.categorias.map((c, ci) => {
            const produtosDaCat = preview.produtos.filter((p) => p.categoriaId === c.id);
            const catAlertas = warnings.categorias[c.id] || [];
            const aberto = catAbertos.includes(c.id);
            return (
              <div key={c.id} className="uploadia-cat-accordion">
                <div
                  className="uploadia-cat-header-item"
                  style={{ padding: '12px 14px', cursor: 'pointer' }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Categoria ${c.nome || 'sem nome'}`}
                  aria-expanded={aberto}
                  onKeyDown={event => {
                    if (event.target !== event.currentTarget) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setCatAbertos(prev => prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id]);
                    }
                  }}
                  onClick={() => setCatAbertos(prev =>
                    prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id]
                  )}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {/* <div
                      className="uploadia-section-icon"
                      style={{ background: '#FFF3E0', color: 'var(--ion-color-primary)' }}
                    >
                      <IonIcon icon={storefrontOutline} />
                    </div> */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="uploadia-cat-header-title">{c.nome || '(sem nome)'}</div>
                      <div className="uploadia-cat-header-badges">
                        <span
                          className={
                            'uploadia-tag ' +
                            (c.tipo === 'pizzas'
                              ? 'uploadia-tag--pizzas'
                              : c.tipo === 'complementos'
                              ? 'uploadia-tag--complementos'
                              : 'uploadia-tag--simples')
                          }
                        >
                          {c.tipo}
                        </span>
                        <span className="uploadia-tag">
                          <IonIcon icon={fastFoodOutline} />
                          {produtosDaCat.length} produto(s)
                        </span>
                        {catAlertas.length > 0 && (
                          <span className="uploadia-tag uploadia-tag--danger">
                            <IonIcon icon={warningOutline} />
                            {catAlertas.length}
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      className="uploadia-header-actions"
                      style={{ display: 'flex', gap: 2, alignItems: 'center', flexShrink: 0 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IonButton
                        size="small"
                        fill="clear"
                        disabled={ci === 0}
                        onClick={(e) => { e.stopPropagation(); moverCategoria(c.id, -1); }}
                      >
                        <IonIcon slot="icon-only" icon={arrowUpOutline} />
                      </IonButton>
                      <IonButton
                        size="small"
                        fill="clear"
                        disabled={ci === preview.categorias.length - 1}
                        onClick={(e) => { e.stopPropagation(); moverCategoria(c.id, 1); }}
                      >
                        <IonIcon slot="icon-only" icon={arrowDownOutline} />
                      </IonButton>
                      <IonButton
                        size="small"
                        fill="clear"
                        color="danger"
                        aria-label={`Remover categoria ${c.nome || 'sem nome'}`}
                        onClick={(e) => { e.stopPropagation(); remCategoria(c.id); }}
                      >
                        <IonIcon slot="icon-only" icon={trashOutline} />
                      </IonButton>
                    </div>
                  </div>
                </div>

                {aberto && (
                <div className="uploadia-category-body">
                  {/* Card: Informações da categoria */}
                  <div className="uploadia-form-card">
                    <div className="uploadia-section-header">
                      <div
                        className="uploadia-section-icon"
                        style={{ background: '#FFF3E0', color: 'var(--ion-color-primary)' }}
                      >
                        <IonIcon icon={listOutline} />
                      </div>
                      <div>
                        <div className="uploadia-section-title">Categoria</div>
                        <div className="uploadia-section-subtitle">Nome e tipo da categoria</div>
                      </div>
                    </div>

                    <div className="uploadia-field">
                      <label className="uploadia-field-label">Nome da categoria</label>
                      <IonInput
                        className="uploadia-field-input"
                        value={c.nome}
                        aria-label="Nome da categoria"
                        onIonChange={(e) => updCategoria(c.id, { nome: e.detail.value || '' })}
                      />
                    </div>

                    <div className="uploadia-field">
                      <label className="uploadia-field-label">Tipo</label>
                      <IonSegment
                        className="uploadia-segment"
                        value={c.tipo}
                        onIonChange={(e) =>
                          updCategoria(c.id, { tipo: (e.detail.value as Categoria['tipo']) || 'simples' })
                        }
                      >
                        <IonSegmentButton value="simples">Simples</IonSegmentButton>
                        <IonSegmentButton value="pizzas">Pizzas</IonSegmentButton>
                        <IonSegmentButton value="complementos">Complementos</IonSegmentButton>
                      </IonSegment>
                    </div>
                  </div>
                  {/* /Card Informações */}

                  {catAlertas.length > 0 && (
                    <div className="uploadia-warning-note uploadia-warning-note--danger">
                      <IonIcon icon={warningOutline} />
                      <span>{catAlertas.join(' • ')}</span>
                    </div>
                  )}

                  {/* Bloco específico de pizzas */}
                  {c.tipo === 'pizzas' && (
                    <>
                      {/* Card: Tamanhos */}
                      <div className="uploadia-form-card">
                        <div className="uploadia-section-header">
                          <div
                            className="uploadia-section-icon"
                            style={{ background: '#F3E5F5', color: '#7B1FA2' }}
                          >
                            <IonIcon icon={pizzaOutline} />
                          </div>
                          <div>
                            <div className="uploadia-section-title">Tamanhos</div>
                            <div className="uploadia-section-subtitle">Defina tamanhos e nº de sabores</div>
                          </div>
                        </div>

                        {c.tamanhos.length === 0 ? (
                          <div className="uploadia-empty-hint">Nenhum tamanho cadastrado</div>
                        ) : (
                          <div className="uploadia-table">
                            <div className="uploadia-table-header">
                              <div className="uploadia-table-col-grow">Nome do tamanho*</div>
                              <div
                                className="uploadia-table-col-num"
                                style={{ width: 140, textAlign: 'center', fontSize: 11 }}
                              >
                                Qtd. sabores*
                              </div>
                              <div className="uploadia-table-col-trash" />
                            </div>
                            {c.tamanhos.map((t, ti) => (
                              <div key={ti} className="uploadia-table-row">
                                <div className="uploadia-table-col-grow">
                                  <IonInput
                                    className="uploadia-cell-input"
                                    placeholder="Ex: Grande"
                                    value={t.name}
                                    onIonChange={(e) => updTamanho(c.id, ti, { name: e.detail.value || '' })}
                                  />
                                </div>
                                <div
                                  className="uploadia-table-col-num"
                                  style={{
                                    width: 140,
                                    flexShrink: 0,
                                    display: 'flex',
                                    gap: 4,
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}
                                >
                                  {[1, 2, 3, 4].map((num) => (
                                    <IonButton
                                      key={num}
                                      size="small"
                                      color={t.sabores === num ? 'danger' : 'light'}
                                      style={
                                        {
                                          '--padding-start': '8px',
                                          '--padding-end': '8px',
                                          minWidth: '26px',
                                          height: '26px',
                                          margin: 0,
                                        } as any
                                      }
                                      onClick={() => updTamanho(c.id, ti, { sabores: num })}
                                    >
                                      {num}
                                    </IonButton>
                                  ))}
                                </div>
                                <div className="uploadia-table-col-trash">
                                  <IonIcon
                                    className="uploadia-trash"
                                    icon={trashOutline}
                                    onClick={() => remTamanho(c.id, ti)}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <IonButton
                          className="uploadia-add-btn"
                          size="small"
                          fill="clear"
                          onClick={() => addTamanho(c.id)}
                        >
                          <IonIcon slot="start" icon={addOutline} />
                          Adicionar tamanho
                        </IonButton>
                      </div>

                      {/* Card: Massas */}
                      <div className="uploadia-form-card">
                        <div className="uploadia-section-header">
                          <div
                            className="uploadia-section-icon"
                            style={{ background: '#F0FDF4', color: '#22C55E' }}
                          >
                            <IonIcon icon={layersOutline} />
                          </div>
                          <div>
                            <div className="uploadia-section-title">Massas</div>
                            <div className="uploadia-section-subtitle">Opções de massa (opcional)</div>
                          </div>
                        </div>

                        {c.massas.length === 0 ? (
                          <div className="uploadia-empty-hint">Nenhuma massa cadastrada</div>
                        ) : (
                          <div className="uploadia-table">
                            <div className="uploadia-table-header">
                              <div className="uploadia-table-col-grow">Nome</div>
                              <div className="uploadia-table-col-price">Preço (R$)</div>
                              <div className="uploadia-table-col-trash" />
                            </div>
                            {c.massas.map((m, mi) => (
                              <div key={mi} className="uploadia-table-row">
                                <div className="uploadia-table-col-grow">
                                  <IonInput
                                    className="uploadia-cell-input"
                                    placeholder="Ex: Tradicional"
                                    value={m.name}
                                    onIonChange={(e) => updMassa(c.id, mi, { name: e.detail.value || '' })}
                                  />
                                </div>
                                <div className="uploadia-table-col-price">
                                  <IonInput
                                    className="uploadia-cell-input-price"
                                    placeholder="0,00"
                                    value={m.price}
                                    onIonChange={(e) =>
                                      updMassa(c.id, mi, { price: e.detail.value || '0,00' })
                                    }
                                  />
                                </div>
                                <div className="uploadia-table-col-trash">
                                  <IonIcon
                                    className="uploadia-trash"
                                    icon={trashOutline}
                                    onClick={() => remMassa(c.id, mi)}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <IonButton
                          className="uploadia-add-btn"
                          size="small"
                          fill="clear"
                          onClick={() => addMassa(c.id)}
                        >
                          <IonIcon slot="start" icon={addOutline} />
                          Adicionar massa
                        </IonButton>
                      </div>

                      {/* Card: Bordas */}
                      <div className="uploadia-form-card">
                        <div className="uploadia-section-header">
                          <div
                            className="uploadia-section-icon"
                            style={{ background: '#EFF6FF', color: '#3B82F6' }}
                          >
                            <IonIcon icon={ellipseOutline} />
                          </div>
                          <div>
                            <div className="uploadia-section-title">Bordas</div>
                            <div className="uploadia-section-subtitle">Opções de borda (opcional)</div>
                          </div>
                        </div>

                        {c.bordas.length === 0 ? (
                          <div className="uploadia-empty-hint">Nenhuma borda cadastrada</div>
                        ) : (
                          <div className="uploadia-table">
                            <div className="uploadia-table-header">
                              <div className="uploadia-table-col-grow">Nome</div>
                              <div className="uploadia-table-col-price">Preço (R$)</div>
                              <div className="uploadia-table-col-trash" />
                            </div>
                            {c.bordas.map((b, bi) => (
                              <div key={bi} className="uploadia-table-row">
                                <div className="uploadia-table-col-grow">
                                  <IonInput
                                    className="uploadia-cell-input"
                                    placeholder="Ex: Catupiry"
                                    value={b.name}
                                    onIonChange={(e) => updBorda(c.id, bi, { name: e.detail.value || '' })}
                                  />
                                </div>
                                <div className="uploadia-table-col-price">
                                  <IonInput
                                    className="uploadia-cell-input-price"
                                    placeholder="0,00"
                                    value={b.price}
                                    onIonChange={(e) =>
                                      updBorda(c.id, bi, { price: e.detail.value || '0,00' })
                                    }
                                  />
                                </div>
                                <div className="uploadia-table-col-trash">
                                  <IonIcon
                                    className="uploadia-trash"
                                    icon={trashOutline}
                                    onClick={() => remBorda(c.id, bi)}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <IonButton
                          className="uploadia-add-btn"
                          size="small"
                          fill="clear"
                          onClick={() => addBorda(c.id)}
                        >
                          <IonIcon slot="start" icon={addOutline} />
                          Adicionar borda
                        </IonButton>
                      </div>

                      {/* Card: Configurações de pizza */}
                      <div className="uploadia-form-card">
                        <div className="uploadia-section-header">
                          <div
                            className="uploadia-section-icon"
                            style={{ background: '#F3F4F6', color: '#6B7280' }}
                          >
                            <IonIcon icon={cogOutline} />
                          </div>
                          <div>
                            <div className="uploadia-section-title">Configurações</div>
                            <div className="uploadia-section-subtitle">Regras de cálculo da pizza</div>
                          </div>
                        </div>

                        <div className="uploadia-toggle-row">
                          <div>
                            <div className="uploadia-toggle-label">Cobrar maior valor entre sabores</div>
                            <div className="uploadia-toggle-desc">
                              Quando ativo, o preço final usa o maior valor entre os sabores.
                            </div>
                          </div>
                          <IonToggle
                            checked={c.cobrarMaiorValor}
                            onIonChange={(e) => updCategoria(c.id, { cobrarMaiorValor: e.detail.checked })}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Card: Produtos */}
                  <div className="uploadia-form-card">
                    <div className="uploadia-section-header">
                      <div
                        className="uploadia-section-icon"
                        style={{ background: '#FFF7ED', color: 'var(--ion-color-primary)' }}
                      >
                        <IonIcon icon={fastFoodOutline} />
                      </div>
                      <div>
                        <div className="uploadia-section-title">Produtos</div>
                        <div className="uploadia-section-subtitle">
                          {produtosDaCat.length} item(ns) nesta categoria
                        </div>
                      </div>
                    </div>

                    {produtosDaCat.length === 0 && (
                      <div className="uploadia-empty-hint">Nenhum produto cadastrado</div>
                    )}
                    {produtosDaCat.map((p, prodOrdem) => {
                      const ehPrimeiro =
                        preview.produtos.find((x) => x.categoriaId === c.id)?.id === p.id;
                      const ehUltimo =
                        [...preview.produtos].reverse().find((x) => x.categoriaId === c.id)?.id === p.id;
                      const prodAlertas = warnings.produtos[p.id] || [];
                      const revisaoPreco = revisarPrecoImportacao(p, preview.produtos, c.tipo);
                      return (
                        <div key={p.id} id={`importacao-produto-${p.id}`} tabIndex={-1} className="uploadia-product-card">
                          <div className="uploadia-product-card-header">
                            <ProdutoImagemPreview src={p.urlImagem} nome={p.nome} />
                            <div className="uploadia-product-heading">
                              <div className="uploadia-product-card-index">{prodOrdem + 1}</div>
                              <div className="uploadia-product-card-title">
                                {p.nome || '(sem nome)'}
                              </div>
                            </div>
                            <div
                              className="uploadia-header-actions"
                              style={{ display: 'flex', gap: 2, alignItems: 'center', flexShrink: 0, marginLeft: 'auto' }}
                            >
                              <IonButton
                                size="small"
                                fill="clear"
                                disabled={ehPrimeiro}
                                onClick={() => moverProduto(p.id, -1)}
                              >
                                <IonIcon slot="icon-only" icon={arrowUpOutline} />
                              </IonButton>
                              <IonButton
                                size="small"
                                fill="clear"
                                disabled={ehUltimo}
                                onClick={() => moverProduto(p.id, 1)}
                              >
                                <IonIcon slot="icon-only" icon={arrowDownOutline} />
                              </IonButton>
                              <IonButton
                                size="small"
                                fill="clear"
                                color="danger"
                                aria-label={`Remover produto ${p.nome || 'sem nome'}`}
                                onClick={() => remProduto(p.id)}
                              >
                                <IonIcon slot="icon-only" icon={trashOutline} />
                              </IonButton>
                            </div>
                          </div>

                          <div className="uploadia-field">
                            <label className="uploadia-field-label">Nome</label>
                            <IonInput
                              className="uploadia-field-input"
                              value={p.nome}
                              aria-label={`Nome do produto ${p.nome || 'sem nome'}`}
                              onIonChange={(e) => updProduto(p.id, { nome: e.detail.value || '' })}
                            />
                          </div>

                          <div className="uploadia-field">
                            <label className="uploadia-field-label">Descrição</label>
                            <IonTextarea
                              className="uploadia-field-textarea"
                              rows={2}
                              value={p.descricao}
                              aria-label={`Descrição do produto ${p.nome || 'sem nome'}`}
                              onIonChange={(e) => updProduto(p.id, { descricao: e.detail.value || '' })}
                            />
                          </div>

                          <div className="uploadia-field">
                            <label className="uploadia-field-label">Categoria do produto</label>
                            <IonSelect className="uploadia-field-input" aria-label={`Categoria do produto ${p.nome || 'sem nome'}`} value={p.categoriaId}
                              onIonChange={event => updProduto(p.id, { categoriaId: event.detail.value })}>
                              {preview.categorias.map(categoria => <IonSelectOption key={categoria.id} value={categoria.id}>{categoria.nome}</IonSelectOption>)}
                            </IonSelect>
                          </div>

                          {c.tipo === 'pizzas' ? (
                            <div className="uploadia-field">
                              <label className="uploadia-field-label">Valores por tamanho</label>
                              {c.tamanhos.length === 0 ? (
                                <div className="uploadia-warning-note">
                                  <IonIcon icon={warningOutline} />
                                  <span>Defina tamanhos na categoria para liberar a edição.</span>
                                </div>
                              ) : (
                                <div className="uploadia-table">
                                  <div className="uploadia-table-header">
                                    <div className="uploadia-table-col-grow">Tamanho</div>
                                    <div className="uploadia-table-col-price">Valor (R$)</div>
                                  </div>
                                  {p.valores.map((v) => (
                                    <div key={v.name} className="uploadia-table-row">
                                      <div className="uploadia-table-col-grow" style={{ fontSize: 13 }}>
                                        {v.name}
                                      </div>
                                      <div className="uploadia-table-col-price">
                                        <IonInput
                                          className="uploadia-cell-input-price"
                                          placeholder="0,00"
                                          value={v.valor}
                                          onIonChange={(e) =>
                                            updValorPizza(p.id, v.name, e.detail.value || '')
                                          }
                                        />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="uploadia-field">
                              <label className="uploadia-field-label">Valor (R$)</label>
                              <IonInput
                                className="uploadia-field-input"
                                placeholder="0,00"
                                value={p.valor}
                                aria-label={`Preço do produto ${p.nome || 'sem nome'}`}
                                onIonChange={(e) => updProduto(p.id, { valor: e.detail.value || '' })}
                              />
                            </div>
                          )}

                          {p.promocao && <div className="uploadia-field">
                            <label className="uploadia-field-label">Preço promocional (R$)</label>
                            <IonInput value={p.promocao.valor} aria-label={`Preço promocional de ${p.nome}`}
                              onIonChange={event => updProduto(p.id, { promocao: { ...p.promocao, valor: event.detail.value || '' } })} />
                            <div className="uploadia-toggle-row">
                              <span>Promoção ativa</span>
                              <IonToggle aria-label={`Promoção ativa de ${p.nome}`} checked={p.promocao.ativa === true}
                                onIonChange={event => updProduto(p.id, { promocao: { ...p.promocao, ativa: event.detail.checked } })} />
                            </div>
                            <p>O preço normal é preservado. A promoção segue os dias, horários e a vigência da origem.</p>
                            {!!p.promocao.diasSemana?.length && <p>Dias: {p.promocao.diasSemana.map((dia: number) => ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][dia]).join(', ')}.</p>}
                            {!!p.promocao.horarios?.length && <p>Horários: {p.promocao.horarios.map((horario: any) => `${horario.inicio}–${horario.fim}`).join(', ')}.</p>}
                            {(p.promocao.inicio || p.promocao.fim) && <p>Vigência: {p.promocao.inicio || 'sem início definido'} até {p.promocao.fim || 'sem término definido'}.</p>}
                          </div>}
                          {revisaoPreco.pendente && <div className="uploadia-warning-note uploadia-price-review" role="note">
                            <IonIcon icon={warningOutline} aria-hidden="true" />
                            <div>
                              <strong>Este produto pode ficar gratuito</strong>
                              <p>O preço base está zerado e os complementos não exigem pagamento. Se o preço vem de um tamanho ou sabor, torne esse grupo obrigatório. Adicionais opcionais não garantem um preço mínimo.</p>
                              {revisaoPreco.gruposSugeridos.map(({ grupo, indice }) => <IonButton key={grupo.id || indice}
                                fill="outline" size="small" disabled={showLoading}
                                aria-label={`Tornar ${grupo.name} obrigatório em ${p.nome}`}
                                onClick={() => updGrupo(p.id, indice, { obrigatoriedade: 'obrigatorio', sizeMin: Math.max(1, Number(grupo.sizeMin) || 0) })}>
                                Tornar “{grupo.name}” obrigatório
                              </IonButton>)}
                              {!!revisaoPreco.gruposSugeridos.length && <p>Escolha o grupo que define o preço. A alteração ficará nesta prévia até confirmar a importação.</p>}
                            </div>
                          </div>}
                          {revisaoPreco.podeSerGratuito && (
                            <label className="uploadia-toggle-row">
                              <span>Confirmo que este produto pode ser gratuito quando não houver escolhas obrigatórias pagas.</span>
                              <IonToggle aria-label={`Gratuidade confirmada de ${p.nome}`} checked={p.gratuitoConfirmado === true}
                                onIonChange={event => updProduto(p.id, { gratuitoConfirmado: event.detail.checked })} />
                            </label>
                          )}
                          {/* Grupos de complementos */}
                          <div style={{ marginTop: 8 }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                marginBottom: 6,
                                fontSize: 12,
                                fontWeight: 700,
                                color: '#555',
                                textTransform: 'uppercase',
                                letterSpacing: '0.03em',
                              }}
                            >
                              <IonIcon icon={listOutline} />
                              Grupos de complementos
                            </div>

                            {p.grupoComplementos.length === 0 && (
                              <div className="uploadia-empty-hint">Sem grupos</div>
                            )}

                            {p.grupoComplementos.map((g, gi) => (
                              <div key={gi} className="uploadia-grupo-card">
                                <div className="uploadia-grupo-card-header">
                                  <div className="uploadia-grupo-card-title">
                                    {g.name || 'Grupo ' + (gi + 1)}
                                  </div>
                                  <IonButton
                                    size="small"
                                    fill="clear"
                                    color="danger"
                                    onClick={() => remGrupo(p.id, gi)}
                                  >
                                    <IonIcon slot="icon-only" icon={trashOutline} />
                                  </IonButton>
                                </div>

                                <div className="uploadia-field">
                                  <label className="uploadia-field-label">Nome do grupo</label>
                                  <IonInput
                                    className="uploadia-field-input"
                                    value={g.name}
                                    aria-label={`Nome do grupo ${g.name || 'sem nome'}`}
                                    onIonChange={(e) =>
                                      updGrupo(p.id, gi, { name: e.detail.value || '' })
                                    }
                                  />
                                </div>

                                <div className="uploadia-field">
                                  <label className="uploadia-field-label">Obrigatoriedade</label>
                                  <IonSegment
                                    className="uploadia-segment"
                                    value={g.obrigatoriedade}
                                    onIonChange={(e) =>
                                      updGrupo(p.id, gi, {
                                        obrigatoriedade:
                                          (e.detail.value as GrupoComplemento['obrigatoriedade']) ||
                                          'opcional',
                                      })
                                    }
                                  >
                                    <IonSegmentButton value="opcional">Opcional</IonSegmentButton>
                                    <IonSegmentButton value="obrigatorio">Obrigatório</IonSegmentButton>
                                  </IonSegment>
                                </div>

                                <div className="uploadia-grupo-row-min-max">
                                  <div>
                                    <label className="uploadia-field-label">Mínimo</label>
                                    <IonInput
                                      className="uploadia-field-input"
                                      type="number"
                                      value={g.sizeMin}
                                      onIonChange={(e) =>
                                        updGrupo(p.id, gi, { sizeMin: Number(e.detail.value) || 0 })
                                      }
                                    />
                                  </div>
                                  <div>
                                    <label className="uploadia-field-label">Máximo (0 = sem limite)</label>
                                    <IonInput
                                      className="uploadia-field-input"
                                      type="number"
                                      value={g.sizeMax}
                                      onIonChange={(e) =>
                                        updGrupo(p.id, gi, { sizeMax: Number(e.detail.value) || 0 })
                                      }
                                    />
                                  </div>
                                </div>

                                <div className="uploadia-field" style={{ marginTop: 10 }}>
                                  <label className="uploadia-field-label">Itens</label>
                                  {g.itens.length === 0 ? (
                                    <div className="uploadia-empty-hint">Sem itens</div>
                                  ) : (
                                    <div className="uploadia-table">
                                      <div className="uploadia-table-header">
                                        <div className="uploadia-table-col-grow">Nome</div>
                                        <div className="uploadia-table-col-price">Preço (R$)</div>
                                        <div className="uploadia-table-col-trash" />
                                      </div>
                                      {g.itens.map((it, ii) => (
                                        <div key={ii} className="uploadia-table-row">
                                          <div className="uploadia-table-col-grow">
                                            <IonInput
                                              className="uploadia-cell-input"
                                              placeholder="Nome"
                                              value={it.name}
                                              onIonChange={(e) =>
                                                updItemGrupo(p.id, gi, ii, {
                                                  name: e.detail.value || '',
                                                })
                                              }
                                            />
                                          </div>
                                          <div className="uploadia-table-col-price">
                                            <IonInput
                                              className="uploadia-cell-input-price"
                                              placeholder="0,00"
                                              value={it.price}
                                              onIonChange={(e) =>
                                                updItemGrupo(p.id, gi, ii, {
                                                  price: e.detail.value || '0,00',
                                                })
                                              }
                                            />
                                          </div>
                                          <div className="uploadia-table-col-trash">
                                            <IonIcon
                                              className="uploadia-trash"
                                              icon={trashOutline}
                                              onClick={() => remItemGrupo(p.id, gi, ii)}
                                            />
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  <IonButton
                                    className="uploadia-add-btn"
                                    size="small"
                                    fill="clear"
                                    onClick={() => addItemGrupo(p.id, gi)}
                                  >
                                    <IonIcon slot="start" icon={addOutline} />
                                    Adicionar item
                                  </IonButton>
                                </div>
                              </div>
                            ))}

                            <IonButton
                              className="uploadia-add-btn"
                              size="small"
                              fill="clear"
                              onClick={() => addGrupo(p.id)}
                            >
                              <IonIcon slot="start" icon={addOutline} />
                              Adicionar grupo
                            </IonButton>
                          </div>

                          {prodAlertas.length > 0 && (
                            <div className="uploadia-warning-note uploadia-warning-note--danger">
                              <IonIcon icon={warningOutline} />
                              <span>{prodAlertas.join(' • ')}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    <IonButton
                      className="uploadia-add-btn"
                      size="small"
                      expand="block"
                      fill="clear"
                      onClick={() => addProduto(c.id)}
                    >
                      <IonIcon slot="start" icon={addOutline} />
                      Adicionar produto
                    </IonButton>
                  </div>
                </div>
                )}
              </div>
            );
          })}
        </div>

        {preview.categorias.length === 0 && (
          <div className="uploadia-empty-state">
            <IonIcon icon={pricetagOutline} />
            <div>Nenhuma categoria. Clique em "Adicionar categoria" para começar.</div>
          </div>
        )}

        <IonLoading key={mensagemLoading} isOpen={showLoading} message={mensagemLoading} spinner="circles" />
      </IonContent>

      <IonFooter className="ion-no-border uploadia-footer">
        <IonToolbar>
          <div className="uploadia-footer-actions">
            <IonButton className="uploadia-cancel-button" fill="outline" disabled={showLoading} onClick={voltar}>
              Cancelar
            </IonButton>
            <IonButton expand="block" size="large" disabled={showLoading || excedeLimite} onClick={() => confirmar()}>
              Confirmar e substituir catálogo
            </IonButton>
          </div>
        </IonToolbar>
      </IonFooter>
    </>
  );
};

export const CatalogoImportacaoPreview: React.FC<CatalogoImportacaoPreviewProps> = ({ catalogo, source, titulo, onBack, onConfirmed }) => (
  <UploadIA closeAction={onConfirmed} closeActionSimple={onBack} importacao={{ catalogo, source, titulo }} />
);

export default UploadIA;
